import React, { useState, useEffect, useMemo } from "react";
import { HandCoins, X, ArrowLeft, ArrowRight, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import SearchableSelect from "./SearchableSelect";
import { toast } from "sonner";
import { BUSINESS_WHATSAPP, buildWhatsAppLink } from "../config/contact";

// =============================================================================
// APPLY-FOR-LOAN — Floating button + 4-step form -> WhatsApp lead
// Number pulled from central /config/contact.js (BUSINESS_WHATSAPP).
// =============================================================================
const LEAD_WHATSAPP_NUMBER = BUSINESS_WHATSAPP;

const EMPLOYMENT_TYPES = ["Salaried", "Self-Employed", "Business Owner", "Other"];
const LOAN_TYPES = ["Home Loan", "Personal Loan", "Business Loan", "Property Loan", "Car Loan", "Education Loan", "Other"];
const LOAN_PURPOSES = ["Buy Property", "Construction", "Renovation", "Business Expansion", "Personal Use", "Other"];
const CONTACT_TIMES = ["Morning", "Afternoon", "Evening", "Other"];

const emptyForm = {
  // step 1
  name: "",
  phone: "",
  employmentType: "",
  employmentTypeOther: "",
  // step 2
  loanType: "",
  loanTypeOther: "",
  loanAmount: "",
  monthlyIncome: "",
  // step 3
  purpose: "",
  purposeOther: "",
  propertyLocation: "",
  // step 4
  reason: "",
  contactTime: "",
  contactTimeOther: "",
};

function stepTitle(step) {
  return ["Basic Details", "Loan Details", "Purpose", "Final Details"][step - 1] || "";
}

/** Resolve the effective value for a dropdown that supports an "Other" input. */
function resolve(baseValue, otherValue) {
  return baseValue === "Other" ? (otherValue || "Other").trim() : baseValue;
}

function OtherAwareSelect({ label, value, other, onValue, onOther, options, placeholder, testid, required }) {
  return (
    <div>
      <Label>{label}{required ? " *" : ""}</Label>
      <div className="mt-1.5">
        <SearchableSelect
          value={value}
          onChange={onValue}
          options={options}
          allowOther={false}
          placeholder={placeholder}
          testid={testid}
        />
      </div>
      {value === "Other" && (
        <div className="mt-2 animate-fade-up">
          <Input
            value={other}
            onChange={(e) => onOther(e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            data-testid={`${testid}-other-input`}
          />
        </div>
      )}
    </div>
  );
}

export default function ApplyForLoan() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) {
      // Reset when closed so next open starts fresh.
      setTimeout(() => { setForm(emptyForm); setStep(1); setSubmitting(false); }, 300);
    }
  }, [open]);

  // -------- validation ---------------------------------------------------------
  const phoneOk = /^[6-9]\d{9}$/.test(form.phone);
  const reasonLen = form.reason.trim().length;

  const step1Valid = useMemo(() => {
    if (!form.name.trim()) return false;
    if (!phoneOk) return false;
    if (!form.employmentType) return false;
    if (form.employmentType === "Other" && !form.employmentTypeOther.trim()) return false;
    return true;
  }, [form.name, phoneOk, form.employmentType, form.employmentTypeOther]);

  const step2Valid = useMemo(() => {
    if (!form.loanType) return false;
    if (form.loanType === "Other" && !form.loanTypeOther.trim()) return false;
    if (!form.loanAmount || Number(form.loanAmount) <= 0) return false;
    if (!form.monthlyIncome || Number(form.monthlyIncome) <= 0) return false;
    return true;
  }, [form.loanType, form.loanTypeOther, form.loanAmount, form.monthlyIncome]);

  const step3Valid = useMemo(() => {
    if (!form.purpose) return false;
    if (form.purpose === "Other" && !form.purposeOther.trim()) return false;
    return true;
  }, [form.purpose, form.purposeOther]);

  const step4Valid = useMemo(() => {
    if (reasonLen < 20) return false;
    if (!form.contactTime) return false;
    if (form.contactTime === "Other" && !form.contactTimeOther.trim()) return false;
    return true;
  }, [reasonLen, form.contactTime, form.contactTimeOther]);

  const currentValid = [step1Valid, step2Valid, step3Valid, step4Valid][step - 1];

  const next = () => {
    if (!currentValid) {
      if (step === 4 && reasonLen < 20) return toast.error(`Please enter at least 20 characters (currently ${reasonLen}).`);
      return toast.error("Please fill all required fields to continue.");
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  // -------- submit --------------------------------------------------------------
  const submit = () => {
    if (!step4Valid) {
      if (reasonLen < 20) return toast.error(`Please enter at least 20 characters (currently ${reasonLen}).`);
      return toast.error("Please complete all fields to continue.");
    }
    setSubmitting(true);

    const employment = resolve(form.employmentType, form.employmentTypeOther);
    const loanType = resolve(form.loanType, form.loanTypeOther);
    const purpose = resolve(form.purpose, form.purposeOther);
    const contactTime = resolve(form.contactTime, form.contactTimeOther);
    const amountFmt = Number(form.loanAmount).toLocaleString("en-IN");
    const incomeFmt = Number(form.monthlyIncome).toLocaleString("en-IN");

    const msg = [
      "New Loan Application:",
      `Name: ${form.name.trim()}`,
      `Phone: +91${form.phone}`,
      `Employment: ${employment}`,
      `Loan Type: ${loanType}`,
      `Amount: ₹${amountFmt}`,
      `Income: ₹${incomeFmt}`,
      `Purpose: ${purpose}`,
      `Property Location: ${form.propertyLocation.trim() || "N/A"}`,
      `Reason: ${form.reason.trim()}`,
      `Preferred Time: ${contactTime}`,
    ].join("\n");

    const url = `https://wa.me/91${LEAD_WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    toast.success("Redirecting to WhatsApp...");
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      setSubmitting(false);
      setOpen(false);
    }, 700);
  };

  // -------- render helpers ------------------------------------------------------
  return (
    <>
      {/* Floating trigger — bottom-right, always visible */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-white px-5 py-3 shadow-[0_10px_30px_-8px_rgba(20,83,45,0.55)] hover:shadow-[0_18px_40px_-8px_rgba(20,83,45,0.65)] transition-all active:scale-[0.97] font-semibold text-sm"
        aria-label="Apply for a Loan"
        data-testid="apply-loan-fab"
      >
        <HandCoins className="h-5 w-5" />
        <span>Apply for Loan</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="apply-loan-modal">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-neutral-100 bg-gradient-to-b from-emerald-50/60 to-white">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <HandCoins className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="font-display font-bold text-lg">Apply for a Loan</DialogTitle>
                  <DialogDescription className="text-xs">Step {step} of 4 — {stepTitle(step)}</DialogDescription>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1.5" data-testid="apply-loan-steps">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= n ? "bg-primary" : "bg-neutral-200"}`} />
                ))}
              </div>
            </DialogHeader>
          </div>

          <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-4" data-testid="apply-step-1">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Enter your full name" className="mt-1.5" data-testid="apply-name" />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground select-none">+91</span>
                    <Input
                      inputMode="numeric"
                      maxLength={10}
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="Enter your active WhatsApp number"
                      className="pl-12"
                      data-testid="apply-phone"
                    />
                  </div>
                  {form.phone && !phoneOk && (
                    <p className="text-xs text-destructive mt-1">Enter a valid 10-digit number starting with 6-9.</p>
                  )}
                </div>
                <OtherAwareSelect
                  label="Employment Type"
                  required
                  value={form.employmentType}
                  other={form.employmentTypeOther}
                  onValue={(v) => set("employmentType", v)}
                  onOther={(v) => set("employmentTypeOther", v)}
                  options={EMPLOYMENT_TYPES}
                  placeholder="Select employment type"
                  testid="apply-employment"
                />
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="space-y-4" data-testid="apply-step-2">
                <OtherAwareSelect
                  label="Loan Type"
                  required
                  value={form.loanType}
                  other={form.loanTypeOther}
                  onValue={(v) => set("loanType", v)}
                  onOther={(v) => set("loanTypeOther", v)}
                  options={LOAN_TYPES}
                  placeholder="Select loan type"
                  testid="apply-loan-type"
                />
                <div>
                  <Label>Loan Amount *</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground select-none">₹</span>
                    <Input inputMode="numeric" value={form.loanAmount} onChange={(e) => set("loanAmount", e.target.value.replace(/\D/g, ""))} placeholder="Enter required loan amount (e.g. 5,00,000)" className="pl-7" data-testid="apply-amount" />
                  </div>
                </div>
                <div>
                  <Label>Monthly Income *</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground select-none">₹</span>
                    <Input inputMode="numeric" value={form.monthlyIncome} onChange={(e) => set("monthlyIncome", e.target.value.replace(/\D/g, ""))} placeholder="Enter your monthly income" className="pl-7" data-testid="apply-income" />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div className="space-y-4" data-testid="apply-step-3">
                <OtherAwareSelect
                  label="Purpose of Loan"
                  required
                  value={form.purpose}
                  other={form.purposeOther}
                  onValue={(v) => set("purpose", v)}
                  onOther={(v) => set("purposeOther", v)}
                  options={LOAN_PURPOSES}
                  placeholder="Select purpose"
                  testid="apply-purpose"
                />
                <div>
                  <Label>Property Location</Label>
                  <Input value={form.propertyLocation} onChange={(e) => set("propertyLocation", e.target.value)} placeholder="Enter city / area (if applicable)" className="mt-1.5" data-testid="apply-property-location" />
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div className="space-y-4" data-testid="apply-step-4">
                <div>
                  <Label>Reason for Loan *</Label>
                  <Textarea
                    rows={4}
                    value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                    placeholder="Explain why you need this loan (minimum 20 characters)"
                    className="mt-1.5"
                    data-testid="apply-reason"
                  />
                  <div className="mt-1 text-xs">
                    <span className={reasonLen < 20 ? "text-destructive font-medium" : "text-emerald-600 font-medium"} data-testid="apply-reason-count">
                      {reasonLen} / 20 characters
                    </span>
                    {reasonLen > 0 && reasonLen < 20 && (
                      <span className="text-muted-foreground ml-2">— Please enter at least 20 characters</span>
                    )}
                  </div>
                </div>
                <OtherAwareSelect
                  label="Preferred Contact Time"
                  required
                  value={form.contactTime}
                  other={form.contactTimeOther}
                  onValue={(v) => set("contactTime", v)}
                  onOther={(v) => set("contactTimeOther", v)}
                  options={CONTACT_TIMES}
                  placeholder="Select preferred time"
                  testid="apply-contact-time"
                />
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50/60 flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button variant="ghost" onClick={back} className="gap-1" data-testid="apply-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            ) : <span />}

            {step < 4 ? (
              <Button
                onClick={next}
                disabled={!currentValid}
                className="bg-primary hover:bg-primary/90 text-white gap-1.5"
                data-testid="apply-next"
              >
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!step4Valid || submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                data-testid="apply-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to WhatsApp...</>
                ) : (
                  <><MessageCircle className="h-4 w-4" /> Submit via WhatsApp</>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
